/**
 * An HTTP failure from one of the dashboard API clients, carrying the status
 * and the server's error code so callers can branch on a code rather than on
 * message text.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/**
 * True when the server said the record is missing. Distinct from a transient
 * failure: retrying cannot bring it back, so a page showing that record has
 * nothing left to show.
 *
 * 404 only, deliberately. A 403 from these routes is usually not about the
 * record at all — `tenantAccessGate` answers 403 `TENANT_DELETED` and the
 * status middleware answers `USER_SUSPENDED`, both account-level states that
 * can recover. Reading those as "gone" would strand the operator on a
 * dead-end retry.
 */
export function isRecordGone(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

/**
 * Reads a failed response from this backend into an `ApiError`.
 *
 * Two envelope shapes reach here: `{error: {code, message}}` from the tenant
 * gate, and `{error: '<text>', code}` from the status middleware.
 *
 * `services/api.ts` deliberately does not use this: it decorates plain Errors
 * with `status`/`code`/`response` and layers 401 and identity-mismatch handling
 * on top, and its callers read those fields. Converting it is its own change.
 */
export async function apiErrorFrom(
  response: Response,
  fallback: string
): Promise<ApiError> {
  const body = await response.json().catch(() => null);
  const message = typeof body?.error === 'string' ? body.error : body?.error?.message;
  const code = body?.error?.code || body?.code;

  return new ApiError(message || fallback, response.status, code);
}
